/**
 * CreateNewLead.jsx — Simplified "+ Create New Lead" page
 * ------------------------------------------------------
 * Owner ask (2026-08-27): make creating a seller lead FAST — 2-3 min max.
 * Property Lookup answers the property facts; the form only asks what
 * the rep learns FROM TALKING TO THE SELLER (motivation / condition /
 * timeline / price / next-action). No ARV / mortgage / beds/baths
 * duplicated fields — those come from the Property Lookup card.
 *
 * Flow: Property Lookup → Seller Contact → Motivation → Condition →
 *       Timeline & Price → Decision Maker & Occupancy → Follow-Up →
 *       CRM Status → Notes → Save
 *
 * Each section is a card — no giant single form. Section expands as
 * data is filled in. Bottom bar has 4 explicit save actions:
 *   SAVE LEAD · SAVE & FOLLOW UP · SAVE & CALL · SEND TO ACQUISITIONS
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import { toast } from "sonner";
import {
  Search, Loader2, MapPin, Home, DollarSign, User, Phone, Mail,
  Flame, Calendar, ClipboardList, ArrowRight, PhoneCall, Send, Plus,
  Wrench, AlertTriangle, Trash2, CheckCircle2,
} from "lucide-react";

// ─── Constants (single source, cheap re-render) ─────────────────────
const LEAD_SOURCES = [
  "PropStream", "BatchDialer", "Inbound Call", "Website",
  "Referral", "Manual", "Other",
];

const MOTIVATION_TAGS = [
  "Vacant", "Tired Landlord", "Tenant Problems", "Inherited", "Probate",
  "Relocating", "Financial Problems", "Behind On Payments", "Pre-Foreclosure",
  "Tax Problems", "Major Repairs", "Code Violations", "Liens", "Divorce",
  "Downsizing", "Cannot Maintain Property", "Already Purchased Another Home",
  "Wants Cash", "Other",
];

const MOTIVATION_LEVELS = [
  { value: "hot",     label: "🔥 Hot" },
  { value: "warm",    label: "Warm" },
  { value: "cold",    label: "Cold" },
  { value: "unknown", label: "Unknown" },
];

const CONDITION_OPTIONS = [
  "Excellent / Updated", "Good", "Average", "Needs Cosmetic Work",
  "Needs Repairs", "Heavy Rehab", "Unknown",
];

const ROOF_OPTIONS  = ["Good / Newer", "Older", "Needs Replacement", "Unknown"];
const AC_OPTIONS    = ["Good / Newer", "Older", "Needs Replacement", "Unknown"];
const KB_OPTIONS    = ["Updated", "Average", "Dated", "Needs Remodel", "Unknown"];

const MAJOR_PROBLEMS = [
  "None", "Roof", "AC", "Plumbing", "Electrical", "Water Damage",
  "Mold", "Foundation", "Pool", "Windows", "Septic / Well", "Other",
];

const TIMELINE_OPTIONS = [
  "ASAP", "Within 7 Days", "Within 14 Days", "Within 30 Days",
  "30–60 Days", "60–90 Days", "3+ Months", "Just Exploring",
];

const FLEX_OPTIONS = ["Yes", "Maybe", "No", "Unknown"];

const OCCUPANCY_OPTIONS = [
  "Owner Occupied", "Tenant Occupied", "Vacant", "Other", "Unknown",
];

const TENANT_OPTIONS = [
  "Good Tenant", "Problem Tenant", "Behind On Rent", "Vacating Soon", "Unknown",
];

const NEXT_ACTIONS = [
  "Call Back", "Send SMS", "Send Email", "Acquisition Review",
  "Make Offer", "Follow-Up Later", "No Follow-Up",
];

const LEAD_STATUSES_FALLBACK = [
  { value: "new_lead",   label: "New Lead" },
  { value: "offer_sent", label: "Offer Made" },
  { value: "follow_up",  label: "Follow-Up" },
  { value: "negotiation", label: "Under Contract" },
  { value: "contract",   label: "Contract Signed" },
  { value: "sold",       label: "Closed" },
  { value: "dead",       label: "Dead" },
];


export default function CreateNewLead() {
  const navigate = useNavigate();

  // ─── Property Lookup ──────────────────────────────────────────────
  const [addr, setAddr]           = useState("");
  const [propData, setPropData]   = useState(null);
  const [lookupBusy, setLookupBusy] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);

  // ─── Seller Contact ───────────────────────────────────────────────
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName]   = useState("");
  const [phones, setPhones]       = useState([""]);
  const [email, setEmail]         = useState("");
  const [source, setSource]       = useState("Manual");

  // ─── Motivation ───────────────────────────────────────────────────
  const [motivationReason, setMotivationReason] = useState("");
  const [motivationTags, setMotivationTags]     = useState([]);
  const [motivationLevel, setMotivationLevel]   = useState("unknown");

  // ─── Condition ────────────────────────────────────────────────────
  const [conditionOverall, setConditionOverall] = useState("Unknown");
  const [roof, setRoof]                         = useState("Unknown");
  const [ac, setAc]                             = useState("Unknown");
  const [kb, setKb]                             = useState("Unknown");
  const [majorProblems, setMajorProblems]       = useState([]);
  const [conditionNotes, setConditionNotes]     = useState("");

  // ─── Timeline ─────────────────────────────────────────────────────
  const [timeline, setTimeline]               = useState("");
  const [timelineNotes, setTimelineNotes]     = useState("");

  // ─── Price ────────────────────────────────────────────────────────
  const [askingPrice, setAskingPrice]         = useState("");
  const [priceFlexible, setPriceFlexible]     = useState("Unknown");
  const [lowestPrice, setLowestPrice]         = useState("");

  // ─── Decision Maker ───────────────────────────────────────────────
  const [decisionShared, setDecisionShared]   = useState("Unknown");
  const [decisionMakerName, setDecisionMakerName] = useState("");

  // ─── Occupancy ────────────────────────────────────────────────────
  const [occupancy, setOccupancy]             = useState("Unknown");
  const [tenantSit, setTenantSit]             = useState("Unknown");

  // ─── Follow-Up ────────────────────────────────────────────────────
  const [nextAction, setNextAction]           = useState("Call Back");
  const [followupDate, setFollowupDate]       = useState("");
  const [followupNotes, setFollowupNotes]     = useState("");

  // ─── CRM Status ───────────────────────────────────────────────────
  // ─── CRM Status ───────────────────────────────────────────────────
  // Options come LIVE from the Lead Management board's columns so any
  // rename / add / remove on the CRM Pipeline flows straight into this
  // dropdown (owner ask 2026-08-27 — dropdown must match the CRM
  // columns exactly). Falls back to canonical stages if the board API
  // is unreachable.
  const [leadStatus, setLeadStatus]           = useState("");
  const [statusOptions, setStatusOptions]     = useState(LEAD_STATUSES_FALLBACK);
  const [primaryBoardId, setPrimaryBoardId]   = useState(null);
  const [acqBoardId, setAcqBoardId]           = useState(null);
  const [assignedTo, setAssignedTo]           = useState("");

  // ─── Notes ────────────────────────────────────────────────────────
  const [convNotes, setConvNotes]             = useState("");

  const [saving, setSaving] = useState(false);

  // ══════════════════════════════════════════════════════════════════
  //  PROPERTY LOOKUP
  // ══════════════════════════════════════════════════════════════════
  const lookupProperty = async (overrideAddr) => {
    // Accept an explicit `overrideAddr` so callers that just picked an
    // autocomplete suggestion can pass the full canonical string
    // WITHOUT waiting for React's setState to flush — otherwise the
    // lookup runs against the rep's still-half-typed input and the
    // Property Snapshot lands with a truncated address (owner report
    // 2026-08-27: "when I click on the auto complete address it don't
    // put all the address only what has been written to that point").
    const query = ((overrideAddr ?? addr) || "").trim();
    if (!query) return toast.error("Enter a property address first");
    setLookupBusy(true);
    try {
      const { data } = await api.post("/properties/enrich", {
        address: query, force: false,
      });
      setPropData(data);
      // Property Lookup should REPLACE the address input with the
      // canonical normalized address it returned (owner ask 2026-08-27
      // "it should auto populate the address also all the information
      // that come with the property ARV Owner information SQF etc").
      const canonicalAddr =
        data?.property_address || data?.address ||
        (data?.address_key ? data.address_key : null);
      if (canonicalAddr) setAddr(canonicalAddr);
      // Pre-fill first/last from owner_name if the seller name is blank.
      const ownerName = data?.owner_name || "";
      if (ownerName && !firstName && !lastName) {
        const parts = ownerName.trim().split(/\s+/);
        setFirstName(parts[0] || "");
        setLastName(parts.slice(1).join(" ") || "");
      }
      // Pre-fill primary phone from skip-trace payload — handle
      // multiple shapes REAPI/RentCast return (phones[0], phone, or
      // phones as string).
      const skipPhones = Array.isArray(data?.phones) ? data.phones : [];
      const p = skipPhones[0] || data?.phone || "";
      if (p && !phones[0]) setPhones([p, ...phones.slice(1)]);
      // If skip-trace found MORE phones, pre-load them into extras.
      if (skipPhones.length > 1) {
        setPhones([
          skipPhones[0] || "",
          ...skipPhones.slice(1, 5),
        ]);
      }
      const emails = Array.isArray(data?.emails) ? data.emails : [];
      const e = emails[0] || data?.email || "";
      if (e && !email) setEmail(e);
      toast.success("Property found — filling in what we know");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Lookup failed";
      toast.error(msg);
      // Still let them proceed manually with just the address.
      setPropData({ property_address: query });
    } finally {
      setLookupBusy(false);
    }
  };

  // Fetch team members once on first render — used for "Assigned To".
  const loadTeamMembers = async () => {
    if (teamMembers.length > 0) return;
    try {
      const { data } = await api.get("/team/members");
      setTeamMembers(Array.isArray(data) ? data : []);
    } catch { /* team endpoint 404 for solo users is fine */ }
  };

  // Load the Lead Management board's live columns so the Lead Status
  // dropdown mirrors the CRM. Fired once on mount.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/boards");
        const boards = data?.boards || [];
        // Cache the Acquisitions board id so "Send to Acquisitions"
        // actually MOVES the lead there (owner ask 2026-08-27 — the
        // save was landing on Lead Management, not Acquisitions, so
        // the rep thought the file wasn't saved).
        const acq = boards.find((b) => (b.name || "").toLowerCase().includes("acquisition"));
        if (alive && acq) setAcqBoardId(acq.id);
        // Prefer the board literally called "Lead Management" — else fall
        // back to the first board the admin owns.
        const pick =
          boards.find((b) => (b.name || "").toLowerCase() === "lead management")
          || boards[0];
        if (!pick || !alive) return;
        setPrimaryBoardId(pick.id);
        const cols = (pick.columns || [])
          .slice()
          .sort((a, b) => (a.order || 0) - (b.order || 0))
          .map((c) => ({ value: c.key, label: c.label || c.name || c.key }));
        if (cols.length) {
          setStatusOptions(cols);
          setLeadStatus(cols[0].value);   // sensible default = 1st column
        }
      } catch { /* fall back to hardcoded 7 stages */ }
    })();
    return () => { alive = false; };
  }, []);

  // ══════════════════════════════════════════════════════════════════
  //  SAVE
  // ══════════════════════════════════════════════════════════════════
  const buildLeadPayload = () => {
    const fullName = `${firstName} ${lastName}`.trim();
    const phone = (phones[0] || "").trim();
    const extras = phones.slice(1).filter((p) => p.trim());

    // Build a rich "conversation summary" note (what the rep learned)
    // — separate from the property facts the lookup already returned.
    const noteBlock = [
      motivationReason && `MOTIVATION: ${motivationReason}`,
      motivationTags.length > 0 && `Tags: ${motivationTags.join(", ")}`,
      motivationLevel !== "unknown" && `Level: ${motivationLevel.toUpperCase()}`,
      "",
      `CONDITION: ${conditionOverall}`,
      `  Roof: ${roof} · AC: ${ac} · K/B: ${kb}`,
      majorProblems.length > 0 && `  Major problems: ${majorProblems.join(", ")}`,
      conditionNotes && `  Notes: ${conditionNotes}`,
      "",
      timeline && `TIMELINE: ${timeline}`,
      timelineNotes && `  ${timelineNotes}`,
      "",
      askingPrice && `ASKING: $${askingPrice} · Flexible: ${priceFlexible}`,
      lowestPrice && `Lowest mentioned: $${lowestPrice}`,
      "",
      decisionShared === "Yes" && `DECISION MAKER: ${decisionMakerName || "someone else involved"}`,
      `OCCUPANCY: ${occupancy}`,
      occupancy === "Tenant Occupied" && tenantSit !== "Unknown" && `  Tenant: ${tenantSit}`,
      "",
      nextAction && `NEXT ACTION: ${nextAction}`,
      followupDate && `  When: ${followupDate}`,
      followupNotes && `  ${followupNotes}`,
      "",
      convNotes && `CONVERSATION NOTES:\n${convNotes}`,
    ]
      .filter(Boolean)
      .join("\n");

    return {
      name: fullName || "New Seller Lead",
      phone,
      email,
      property_address:
        propData?.property_address || propData?.address || addr,
      source_tag: source,
      list_name: source === "Website" ? "Website" : "Manual Entry",
      // The `stage` field only accepts the 7 canonical backend stages,
      // so leave it at "new_lead" here — we'll drop the lead into the
      // rep's chosen CRM COLUMN via `/boards/{bid}/move-lead` right
      // after create so the dropdown pick actually places the card
      // where the rep expects it.
      stage: "new_lead",
      board_id: primaryBoardId || undefined,
      column_key: leadStatus || undefined,
      motivation: motivationReason,
      motivation_tags: motivationTags,
      motivation_level: motivationLevel,
      condition_overall: conditionOverall,
      condition_roof: roof,
      condition_ac: ac,
      condition_kb: kb,
      condition_problems: majorProblems,
      condition_notes: conditionNotes,
      timeline_bucket: timeline,
      asking_price: askingPrice ? parseInt(askingPrice.replace(/\D/g, ""), 10) : null,
      price_flexible: priceFlexible,
      lowest_price_mentioned: lowestPrice ? parseInt(lowestPrice.replace(/\D/g, ""), 10) : null,
      occupancy,
      tenant_situation: occupancy === "Tenant Occupied" ? tenantSit : null,
      decision_shared: decisionShared,
      decision_maker_name: decisionMakerName,
      next_action: nextAction,
      next_followup_at: followupDate || null,
      followup_notes: followupNotes,
      extra_phones: extras,
      assigned_to: assignedTo || undefined,
      notes: noteBlock,
      property_lookup_snapshot: propData || null,
    };
  };

  const save = async (mode = "save") => {
    if (!addr && !propData?.property_address) {
      return toast.error("Property address is required — use Look Up above");
    }
    if (!(firstName || lastName) && !propData?.owner_name) {
      return toast.error("Seller name is required");
    }
    setSaving(true);
    try {
      const payload = buildLeadPayload();
      const { data: lead } = await api.post("/leads", payload);
      // Drop the new lead into the exact CRM column the rep picked.
      // Silent-fail — the lead is still saved even if the board move
      // fails (e.g. tenant scoping edge cases).
      if (primaryBoardId && leadStatus) {
        try {
          await api.post(`/boards/${primaryBoardId}/move-lead`, {
            lead_id: lead.id, column_key: leadStatus,
          });
        } catch { /* soft-fail */ }
      }
      toast.success("Lead saved");
      if (mode === "call") {
        navigate(`/dialer?lead_id=${lead.id}`);
      } else if (mode === "acquisitions") {
        // Send to Acquisitions — MOVE the lead to the Acquisitions
        // board (its first column) so it actually shows up in the
        // Acquisitions pipeline, not just gets tagged. Owner report
        // 2026-08-27: previous PATCH-with-tag was silently dropped
        // by the backend (LeadIn schema has `list_tag`, not `tag`),
        // so the rep landed on /boards with the lead still sitting
        // on Lead Management. Fix: real move + navigate directly to
        // the Acquisitions board with the lead drawer pre-opened.
        if (acqBoardId) {
          try {
            await api.post(`/boards/${acqBoardId}/move-lead`, {
              lead_id: lead.id, column_key: undefined,
            });
          } catch { /* soft-fail — lead is still saved */ }
          toast.success("Sent to Acquisitions");
          navigate(`/boards?board_id=${acqBoardId}&lead_id=${lead.id}`);
        } else {
          toast.success("Lead saved — Acquisitions board not found, showing Pipeline");
          navigate(`/boards?lead_id=${lead.id}`);
        }
      } else if (mode === "follow_up") {
        navigate(`/boards?board_id=${primaryBoardId || ""}&lead_id=${lead.id}`);
      } else {
        // Default "Save Lead" → land on the pipeline board with the
        // lead drawer pre-opened so the rep confirms it landed.
        navigate(`/boards?board_id=${primaryBoardId || ""}&lead_id=${lead.id}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  // ══════════════════════════════════════════════════════════════════
  return (
    <div className="p-6 space-y-6 max-w-5xl mx-auto" data-testid="create-new-lead-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <Plus className="text-fuchsia-500" size={28} />
          Create New Lead
        </h1>
        <div className="text-xs text-muted-foreground mt-1">
          Property Lookup fills the facts. You only enter what you learn from talking to the seller.
        </div>
      </div>

      {/* ─── 1. PROPERTY LOOKUP ─── */}
      <Card
        title="Property Lookup"
        subtitle="Start here — every downstream question fills in from what we find."
        icon={<MapPin size={18} className="text-fuchsia-500" />}
        allowOverflow
      >
        <div className="flex flex-col md:flex-row gap-2">
          <AddressAutocomplete
            value={addr}
            onChange={setAddr}
            onPick={(item) => {
              // AddressAutocomplete already writes the canonical
              // address via onChange, but that's an async setState —
              // pass the string directly into lookupProperty so the
              // enrich API call uses the FULL address, not whatever
              // React's `addr` state happens to hold this tick.
              const canonical = item?.address || item?.title || "";
              if (canonical) setAddr(canonical);
              lookupProperty(canonical || undefined);
            }}
            onEnter={lookupProperty}
            className="flex-1"
            testid="create-lead-address"
          />
          <Button
            onClick={lookupProperty}
            disabled={lookupBusy || !addr.trim()}
            className="min-w-[180px]"
            data-testid="create-lead-lookup-btn"
          >
            {lookupBusy
              ? <><Loader2 className="animate-spin mr-2" size={16} />Looking up…</>
              : <><Search size={16} className="mr-2" />Look up property</>}
          </Button>
        </div>

        {propData && <PropertySummaryCard data={propData} />}
      </Card>

      {/* ─── 2. SELLER CONTACT ─── */}
      <Card title="Seller Contact" icon={<User size={18} className="text-sky-500" />}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <FieldInput label="First name" value={firstName} onChange={setFirstName}
                       testid="create-lead-first-name" />
          <FieldInput label="Last name" value={lastName} onChange={setLastName}
                       testid="create-lead-last-name" />
        </div>
        <div className="space-y-2 mt-3">
          <Label className="text-xs uppercase tracking-widest text-slate-500">Phones</Label>
          {phones.map((p, i) => (
            <div key={i} className="flex gap-2">
              <Input
                value={p}
                onChange={(e) => {
                  const next = [...phones];
                  next[i] = e.target.value;
                  setPhones(next);
                }}
                placeholder={i === 0 ? "Primary phone" : `Phone ${i + 1}`}
                data-testid={`create-lead-phone-${i}`}
              />
              {i > 0 && (
                <Button
                  variant="ghost" size="icon"
                  onClick={() => setPhones(phones.filter((_, j) => j !== i))}
                >
                  <Trash2 size={14} className="text-red-500" />
                </Button>
              )}
            </div>
          ))}
          <Button
            variant="outline" size="sm"
            onClick={() => setPhones([...phones, ""])}
            data-testid="create-lead-add-phone-btn"
          >
            <Plus size={13} className="mr-1" /> Add another phone
          </Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
          <FieldInput label="Email" value={email} onChange={setEmail}
                       testid="create-lead-email" />
          <SelectField label="Lead Source" value={source} onChange={setSource}
                        options={LEAD_SOURCES.map((s) => ({ value: s, label: s }))}
                        testid="create-lead-source" />
        </div>
      </Card>

      {/* ─── 3. MOTIVATION ─── */}
      <Card title="Seller Motivation" icon={<Flame size={18} className="text-red-500" />}>
        <Label className="text-xs uppercase tracking-widest text-slate-500">
          Why are you considering selling?
        </Label>
        <Textarea
          value={motivationReason}
          onChange={(e) => setMotivationReason(e.target.value)}
          rows={2}
          className="mt-1"
          placeholder="In the seller's own words…"
          data-testid="create-lead-motivation-reason"
        />
        <Label className="text-xs uppercase tracking-widest text-slate-500 mt-3 block">
          Motivation tags (multi-select)
        </Label>
        <div className="flex flex-wrap gap-1.5 mt-1">
          {MOTIVATION_TAGS.map((t) => {
            const on = motivationTags.includes(t);
            return (
              <button
                key={t}
                type="button"
                onClick={() =>
                  setMotivationTags(on
                    ? motivationTags.filter((x) => x !== t)
                    : [...motivationTags, t])}
                className={`px-2.5 py-1 rounded-full text-xs font-medium border-2 transition ${
                  on
                    ? "bg-red-100 border-red-300 text-red-800"
                    : "bg-white border-slate-200 text-slate-600 hover:border-red-200"
                }`}
                data-testid={`create-lead-motivation-tag-${t.toLowerCase().replace(/\s+/g,"-")}`}
              >
                {t}
              </button>
            );
          })}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
          <SelectField label="Motivation Level" value={motivationLevel}
                        onChange={setMotivationLevel}
                        options={MOTIVATION_LEVELS}
                        testid="create-lead-motivation-level" />
        </div>
      </Card>

      {/* ─── 4. CONDITION ─── */}
      <Card title="Property Condition" icon={<Wrench size={18} className="text-amber-500" />}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <SelectField label="Overall Condition" value={conditionOverall}
                        onChange={setConditionOverall}
                        options={CONDITION_OPTIONS.map((o) => ({ value: o, label: o }))}
                        testid="create-lead-condition-overall" />
          <SelectField label="Roof" value={roof} onChange={setRoof}
                        options={ROOF_OPTIONS.map((o) => ({ value: o, label: o }))}
                        testid="create-lead-roof" />
          <SelectField label="AC" value={ac} onChange={setAc}
                        options={AC_OPTIONS.map((o) => ({ value: o, label: o }))}
                        testid="create-lead-ac" />
          <SelectField label="Kitchen & Bathrooms" value={kb} onChange={setKb}
                        options={KB_OPTIONS.map((o) => ({ value: o, label: o }))}
                        testid="create-lead-kb" />
        </div>
        <Label className="text-xs uppercase tracking-widest text-slate-500 mt-3 block">
          Any major problems? (multi-select)
        </Label>
        <div className="flex flex-wrap gap-1.5 mt-1">
          {MAJOR_PROBLEMS.map((p) => {
            const on = majorProblems.includes(p);
            return (
              <button key={p} type="button"
                onClick={() => setMajorProblems(on
                  ? majorProblems.filter((x) => x !== p)
                  : [...majorProblems, p])}
                className={`px-2.5 py-1 rounded-full text-xs font-medium border-2 transition ${
                  on
                    ? "bg-amber-100 border-amber-300 text-amber-800"
                    : "bg-white border-slate-200 text-slate-600 hover:border-amber-200"
                }`}
                data-testid={`create-lead-problem-${p.toLowerCase().replace(/[\s\/]+/g,"-")}`}
              >
                {p}
              </button>
            );
          })}
        </div>
        <Textarea
          value={conditionNotes}
          onChange={(e) => setConditionNotes(e.target.value)}
          rows={2}
          className="mt-3"
          placeholder="Condition notes (what did the seller say?)"
          data-testid="create-lead-condition-notes"
        />
      </Card>

      {/* ─── 5. TIMELINE & PRICE ─── */}
      <Card title="Timeline & Price" icon={<Calendar size={18} className="text-emerald-500" />}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <SelectField label="When would you like to sell?" value={timeline}
                        onChange={setTimeline}
                        options={TIMELINE_OPTIONS.map((o) => ({ value: o, label: o }))}
                        testid="create-lead-timeline" />
          <FieldInput label="Why that timeline? (optional)"
                       value={timelineNotes} onChange={setTimelineNotes}
                       testid="create-lead-timeline-notes" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
          <FieldInput label="Asking price ($)" value={askingPrice}
                       onChange={setAskingPrice} placeholder="225000"
                       testid="create-lead-asking-price" />
          <SelectField label="Flexible?" value={priceFlexible}
                        onChange={setPriceFlexible}
                        options={FLEX_OPTIONS.map((o) => ({ value: o, label: o }))}
                        testid="create-lead-flexible" />
          <FieldInput label="Lowest mentioned ($)" value={lowestPrice}
                       onChange={setLowestPrice}
                       testid="create-lead-lowest-price" />
        </div>
      </Card>

      {/* ─── 6. DECISION MAKER & OCCUPANCY ─── */}
      <Card title="Decision Maker & Occupancy"
             icon={<ClipboardList size={18} className="text-violet-500" />}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <SelectField label="Anyone else involved in the sale decision?"
                        value={decisionShared} onChange={setDecisionShared}
                        options={["Yes", "No", "Unknown"].map((o) => ({ value: o, label: o }))}
                        testid="create-lead-decision-shared" />
          {decisionShared === "Yes" && (
            <FieldInput label="Name / Relationship"
                         value={decisionMakerName} onChange={setDecisionMakerName}
                         testid="create-lead-decision-maker" />
          )}
          <SelectField label="Property currently"
                        value={occupancy} onChange={setOccupancy}
                        options={OCCUPANCY_OPTIONS.map((o) => ({ value: o, label: o }))}
                        testid="create-lead-occupancy" />
          {occupancy === "Tenant Occupied" && (
            <SelectField label="Tenant situation"
                          value={tenantSit} onChange={setTenantSit}
                          options={TENANT_OPTIONS.map((o) => ({ value: o, label: o }))}
                          testid="create-lead-tenant" />
          )}
        </div>
      </Card>

      {/* ─── 7. FOLLOW-UP ─── */}
      <Card title="Follow-Up" icon={<Calendar size={18} className="text-sky-500" />}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <SelectField label="Next action" value={nextAction} onChange={setNextAction}
                        options={NEXT_ACTIONS.map((o) => ({ value: o, label: o }))}
                        testid="create-lead-next-action" />
          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-500">
              When
            </Label>
            <Input type="datetime-local" value={followupDate}
                    onChange={(e) => setFollowupDate(e.target.value)}
                    data-testid="create-lead-followup-date" />
          </div>
        </div>
        <Textarea
          value={followupNotes}
          onChange={(e) => setFollowupNotes(e.target.value)}
          rows={2}
          className="mt-3"
          placeholder="Follow-up notes"
          data-testid="create-lead-followup-notes"
        />
      </Card>

      {/* ─── 8. CRM STATUS ─── */}
      <Card title="CRM Status" icon={<CheckCircle2 size={18} className="text-emerald-500" />}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <SelectField label="Lead Status" value={leadStatus} onChange={setLeadStatus}
                        options={statusOptions}
                        testid="create-lead-status" />
          <div onFocus={loadTeamMembers}>
            <SelectField label="Assigned to (optional)" value={assignedTo}
                          onChange={setAssignedTo}
                          options={[
                            { value: "",  label: "— Unassigned —" },
                            ...teamMembers.map((u) => ({
                              value: u.id, label: u.name || u.email || u.id.slice(0, 8),
                            })),
                          ]}
                          testid="create-lead-assigned-to" />
          </div>
        </div>
      </Card>

      {/* ─── 9. CONVERSATION NOTES ─── */}
      <Card title="Conversation Notes"
             icon={<ClipboardList size={18} className="text-slate-500" />}>
        <Textarea
          value={convNotes}
          onChange={(e) => setConvNotes(e.target.value)}
          rows={5}
          placeholder="Seller inherited property from mother. House vacant 8 months. Roof old. Wants around $225K but said maybe some flexibility. Wants to sell within 30 days."
          data-testid="create-lead-conv-notes"
        />
      </Card>

      {/* ─── SAVE BAR ─── */}
      <div className="sticky bottom-0 z-10 -mx-6 px-6 py-4 bg-white/95 backdrop-blur border-t-2 border-slate-200 shadow-2xl">
        <div className="max-w-5xl mx-auto flex flex-wrap gap-2 justify-end">
          <Button
            variant="outline" size="lg"
            onClick={() => save("save")}
            disabled={saving}
            data-testid="create-lead-save-btn"
          >
            {saving && <Loader2 className="animate-spin mr-2" size={16} />}
            Save Lead
          </Button>
          <Button
            variant="outline" size="lg"
            onClick={() => save("follow_up")}
            disabled={saving}
            data-testid="create-lead-save-followup-btn"
          >
            <Calendar size={16} className="mr-2" /> Save & Follow Up
          </Button>
          <Button
            size="lg"
            onClick={() => save("call")}
            disabled={saving}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="create-lead-save-call-btn"
          >
            <PhoneCall size={16} className="mr-2" /> Save & Call
          </Button>
          <Button
            size="lg"
            onClick={() => save("acquisitions")}
            disabled={saving}
            className="bg-fuchsia-600 hover:bg-fuchsia-700"
            data-testid="create-lead-save-acquisitions-btn"
          >
            <Send size={16} className="mr-2" /> Send to Acquisitions
            <ArrowRight size={14} className="ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
}


// ══════════════════════════════════════════════════════════════════════
//  REUSABLE PIECES
// ══════════════════════════════════════════════════════════════════════
function Card({ title, subtitle, icon, children, allowOverflow = false }) {
  return (
    <section
      className={`rounded-2xl border-2 border-slate-200 bg-white shadow-sm ${
        allowOverflow ? "" : "overflow-hidden"
      }`}
    >
      <header className="px-4 py-3 border-b bg-slate-50 flex items-center gap-2">
        {icon}
        <h2 className="font-semibold text-slate-800">{title}</h2>
        {subtitle && <span className="text-[11px] text-muted-foreground ml-2">{subtitle}</span>}
      </header>
      <div className="p-4">{children}</div>
    </section>
  );
}

function FieldInput({ label, value, onChange, placeholder, testid }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-widest text-slate-500">{label}</Label>
      <Input
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1"
        data-testid={testid}
      />
    </div>
  );
}

function SelectField({ label, value, onChange, options, testid }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-widest text-slate-500">{label}</Label>
      <Select value={value || undefined} onValueChange={onChange}>
        <SelectTrigger className="mt-1" data-testid={testid}>
          <SelectValue placeholder="—" />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem key={o.value || "_none"} value={o.value || "_none"}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}


function PropertySummaryCard({ data }) {
  // REAPI / RentCast return the address on `address`; the user_saved
  // shortcut path returns it on `property_address`. Accept either.
  const rows = [
    ["Owner",           data.owner_name],
    ["Property Address", data.property_address || data.address],
    ["Mailing Address", data.mailing_address],
    ["Property Value",  data.property_value != null ? money(data.property_value) : null],
    ["ARV",             data.arv != null ? money(data.arv) : null],
    ["Equity",          data.equity != null ? money(data.equity) : null],
    ["Mortgage",        data.mortgage_balance != null ? money(data.mortgage_balance) : null],
    ["Loan Type",       data.loan_type],
    ["Interest Rate",   data.interest_rate != null ? `${data.interest_rate}%` : null],
    ["Beds / Baths",    [data.bedrooms, data.bathrooms].filter((x) => x != null && x !== "").join(" / ") || null],
    ["Sq Ft",           data.sqft],
    ["Lot (acres)",     data.lot_size],
    ["Year Built",      data.year_built],
    ["Property Type",   data.property_type],
    ["Last Sale",       data.last_sale_price != null ? money(data.last_sale_price) : null],
    ["Days Owned",      data.days_owned],
    ["Property Taxes",  data.property_taxes != null ? money(data.property_taxes) : null],
    ["HOA",             data.hoa != null ? money(data.hoa) : null],
    ["Rent Est.",       data.rental_estimate != null ? money(data.rental_estimate) : null],
    ["Flood Zone",      data.flood_zone],
  ].filter(([, v]) => v !== null && v !== undefined && v !== "");

  const flags = [
    ["vacant",             "Vacant"],
    ["tax_delinquent",     "Tax Delinquent"],
    ["out_of_state_owner", "Out-of-State Owner"],
    ["probate",            "Probate"],
    ["code_violations",    "Code Violations"],
    ["foreclosure",        "Foreclosure"],
    ["liens",              "Liens"],
  ].filter(([k]) => data[k]);

  return (
    <div className="mt-4 rounded-xl border-2 border-fuchsia-200 bg-fuchsia-50/40 p-4"
          data-testid="create-lead-property-summary">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold text-fuchsia-700 mb-2">
        <Home size={12} />
        Property Snapshot
      </div>
      {rows.length === 0 ? (
        <div className="text-xs text-muted-foreground">
          No property data found — you can still enter seller info manually.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
          {rows.map(([k, v]) => (
            <div key={k} className="min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">{k}</div>
              <div className="text-slate-800 font-medium truncate" title={v}>{v}</div>
            </div>
          ))}
        </div>
      )}
      {flags.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-3">
          {flags.map(([, label]) => (
            <span key={label} className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest border border-red-300 bg-red-50 text-red-700 flex items-center gap-1">
              <AlertTriangle size={9} />
              {label}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
